import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEye } from "@fortawesome/free-solid-svg-icons";
import { formatLocalDate, formatFileSize } from "./format";
import openFileModal from "./openFileModal";

const SUBS = { CHAT: "chat", OCR: "ocr" };

const FileTable = ({ list, emptyMsg }) => {
  if (!list || list.length === 0) {
    return (
      <div className="empty-state empty-state-sm">
        <div className="es-msg">{emptyMsg}</div>
      </div>
    );
  }
  return (
    <table className="ch-table ch-table-rec">
      <thead>
        <tr>
          <th>File Name</th>
          <th>Created</th>
          <th>Size</th>
          <th className="ch-th-actions" aria-label="Actions" />
        </tr>
      </thead>
      <tbody>
        {list.map((file, i) => {
          const url = file.fileUrl || file.filePath || "";
          const name = file.fileName || `File ${i + 1}`;
          const date = formatLocalDate(file.createDateTime || file.createdAt);
          const size = formatFileSize(file.size ?? file.fileSize);
          return (
            <tr key={url || i}>
              <td className="ch-td-name" title={name}>
                {url ? (
                  <button
                    type="button"
                    className="ch-name-btn"
                    onClick={() => openFileModal(url, name)}
                  >
                    <span className="ch-file-name">{name}</span>
                  </button>
                ) : (
                  <span className="ch-file-name">{name}</span>
                )}
              </td>
              <td className="ch-td-date">{date}</td>
              <td className="ch-td-size">{size}</td>
              <td className="ch-td-actions">
                {url && (
                  <div className="ch-actions">
                    <button
                      type="button"
                      className="ch-action"
                      title="View file"
                      aria-label="View file"
                      onClick={() => openFileModal(url, name)}
                    >
                      <FontAwesomeIcon icon={faEye} className="ch-action-icon" />
                      <span className="ch-action-label">View</span>
                    </button>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ch-action"
                      title="Download file"
                      aria-label="Download file"
                      download
                    >
                      <FontAwesomeIcon icon={faDownload} className="ch-action-icon" />
                      <span className="ch-action-label">Download</span>
                    </a>
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const FilesPane = ({ files, isLoading, error, onRetry }) => {
  const [active, setActive] = useState(SUBS.CHAT);

  if (isLoading) {
    return (
      <div className="loading-state">
        <div className="spinner" />
        <div>Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ch-error">
        {error}
        {onRetry && (
          <div style={{ marginTop: 8 }}>
            <button type="button" className="ch-refresh-btn" onClick={onRetry}>
              Retry
            </button>
          </div>
        )}
      </div>
    );
  }

  // Backend shape varies between deployments — read every known alias so the
  // tab works against current dev + any pending API rename.
  const data = files || {};
  const chatList = data.chatList || data.chatFiles || data.chats || [];
  const ocrList = data.ocrList || data.ocrFiles || data.extractedTextList || [];

  if (chatList.length === 0 && ocrList.length === 0) {
    return (
      <div className="empty-state">
        <div className="es-msg">
          No files available for this session or may have been deleted by your Admin.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="file-subtabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={"file-subtab" + (active === SUBS.CHAT ? " active" : "")}
          onClick={() => setActive(SUBS.CHAT)}
        >
          Chat ({chatList.length})
        </button>
        <button
          type="button"
          role="tab"
          className={"file-subtab" + (active === SUBS.OCR ? " active" : "")}
          onClick={() => setActive(SUBS.OCR)}
        >
          Extracted text ({ocrList.length})
        </button>
      </div>
      <div className="file-subpane">
        {active === SUBS.CHAT && (
          <FileTable list={chatList} emptyMsg="No chat files available." />
        )}
        {active === SUBS.OCR && (
          <FileTable list={ocrList} emptyMsg="No extracted text files available." />
        )}
      </div>
    </div>
  );
};

export default FilesPane;
